import React from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, ArrowRight, ClipboardCheck, Target, Monitor, Newspaper } from 'lucide-react';
import { TrainingThemes } from '../components/TrainingThemes';

export const Home: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Compact Layout */}
      <div className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="text-center text-white">
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              Digitale Vaardigheden Ontwikkelen
            </h1>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Ontwikkel je AI-, digitale- en mediawijsheid vaardigheden met onze complete suite
            </p>
            
            {/* Primary CTA */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link 
                to="/topics" 
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-300 shadow-lg"
              >
                <GraduationCap className="w-5 h-5 mr-2" />
                Bekijk Alle Trainingen
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link 
                to="#suites" 
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white border border-white/20 rounded-xl font-semibold hover:bg-white/20 transition-all duration-300"
              >
                <Target className="w-5 h-5 mr-2" />
                Ontdek de Suites
              </Link>
            </div>

            {/* Compact Stats */}
            <div className="flex justify-center space-x-8 text-sm">
              <div className="text-center">
                <div className="text-2xl font-bold">3</div>
                <div className="text-blue-200">Suites</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">15+</div>
                <div className="text-blue-200">Tools</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">500+</div>
                <div className="text-blue-200">Vragen</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Suite Tools Section */}
      <div id="suites" className="bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              AI-Geletterdheid Tools
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Kies de tool die bij je past om je AI-vaardigheden te ontwikkelen
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* AI Geletterdheid Scan */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  AI Geletterdheid Scan
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  10 Likert-schaal vragen om je AI-niveau te bepalen
                </p>
                <Link
                  to="/ai-literacy-scan"
                  className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  <Target className="w-4 h-4 mr-2" />
                  Start Scan
                </Link>
              </div>
            </div>

            {/* AI Kennistoets */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="text-center">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <ClipboardCheck className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  AI Kennistoets
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  20 scenario-vragen om je concrete AI-kennis te testen
                </p>
                <Link
                  to="/self-assessment"
                  className="inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
                >
                  <ClipboardCheck className="w-4 h-4 mr-2" />
                  Start Toets
                </Link>
              </div>
            </div>

            {/* AI Training */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <GraduationCap className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  AI Training
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  6 modules met 275 vragen om je AI-vaardigheden te ontwikkelen
                </p>
                <Link
                  to="/topics"
                  className="inline-flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
                >
                  <GraduationCap className="w-4 h-4 mr-2" />
                  Start AI Training
                </Link>
              </div>
            </div>

          </div>

        </div>
      </div>

      {/* Digital Suite Tools Section */}
      <div className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Digitale Geletterdheid Tools
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Kies de tool die bij je past om je digitale vaardigheden te ontwikkelen
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Digitale Geletterdheid Scan */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Monitor className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Digitale Geletterdheid Scan
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  30 Likert-schaal vragen om je digitale niveau te bepalen
                </p>
                <Link
                  to="/digital-literacy-scan"
                  className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  <Monitor className="w-4 h-4 mr-2" />
                  Start Scan
                </Link>
              </div>
            </div>

            {/* Digitale Kennistoets */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="text-center">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <ClipboardCheck className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Digitale Kennistoets
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  20 scenario-vragen om je concrete digitale kennis te testen
                </p>
                <Link
                  to="/topics?theme=digitale-kennistoets"
                  className="inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
                >
                  <ClipboardCheck className="w-4 h-4 mr-2" />
                  Start Toets
                </Link>
              </div>
            </div>

            {/* Digitale Geletterdheid Training */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <GraduationCap className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Digitale Geletterdheid Training
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  8 modules met 50 vragen om je digitale vaardigheden te ontwikkelen
                </p>
                <Link
                  to="/topics?theme=digitale-geletterdheid"
                  className="inline-flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
                >
                  <GraduationCap className="w-4 h-4 mr-2" />
                  Start Training
                </Link>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Media Suite Tools Section */}
      <div className="bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Mediawijsheid Tools
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Test je kennis over nepnieuws, filterbubbels en kritisch mediagedrag
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Mediawijsheid Scan */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Newspaper className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Mediawijsheid Scan
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  18 vragen over nepnieuws, algoritmes en verantwoordelijk mediagebruik
                </p>
                <Link
                  to="/media-literacy-scan"
                  className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  <Newspaper className="w-4 h-4 mr-2" />
                  Start Scan
                </Link>
              </div>
            </div>

            {/* Mediawijsheid Kennistoets */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="text-center">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <ClipboardCheck className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Mediawijsheid Kennistoets
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  27 vragen over nepnieuws, algoritmes en mediawijsheid
                </p>
                <Link
                  to="/media-wisdom-assessment"
                  className="inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
                >
                  <ClipboardCheck className="w-4 h-4 mr-2" />
                  Start Kennistoets
                </Link>
              </div>
            </div>

            {/* Mediawijsheid Training */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <GraduationCap className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Mediawijsheid Training
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Leer kritisch omgaan met media, nepnieuws herkennen en verantwoordelijk mediagebruik
                </p>
                <Link
                  to="/topics?theme=mediawijsheid"
                  className="inline-flex items-center justify-center px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
                >
                  <GraduationCap className="w-4 h-4 mr-2" />
                  Start Training
                </Link>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Training Themes Section */}
      <div className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Trainingen
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Ontwikkel je digitale kennis en vaardigheden met onze uitgebreide trainingen
            </p>
          </div>
          <TrainingThemes />
        </div>
      </div>
    </div>
  );
};
